import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { CouponAPI } from '../services/api';

const Cart = () => {
  const { cart, dispatch } = useCart();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const handleUpdateQuantity = (id, quantity) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id, quantity }
    });
  };

  const handleRemoveItem = (id) => {
    dispatch({
      type: 'REMOVE_ITEM',
      payload: id
    });
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleCouponChange = (e) => {
    setCouponCode(e.target.value);
    setCouponError('');
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Моля, въведете код за купон');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const response = await CouponAPI.validateCoupon(couponCode, cart.items);
      
      if (response.valid) {
        dispatch({
          type: 'APPLY_COUPON',
          payload: response.coupon
        });
        setCouponCode('');
      }
    } catch (error) {
      setCouponError(error.response?.data?.error || 'Невалиден код за купон');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    dispatch({
      type: 'REMOVE_COUPON'
    });
  };

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto my-16 p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Вашата кошница е празна</h2>
        <p className="text-gray-600 mb-8">Добавете продукти в кошницата, за да продължите с пазаруването.</p>
        <Link to="/" className="bg-primary text-white px-6 py-3 rounded-md hover:bg-primary/80 transition">
          Продължете с пазаруването
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-8">
      <h2 className="text-2xl font-bold mb-8">Вашата кошница за пазаруване</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {cart.items.map((item) => (
            <div key={item.id} className="flex items-center border-b border-gray-200 py-5 relative">
              <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-md overflow-hidden mr-4">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Няма изображение</div>
                )}
              </div>
              
              <div className="flex-grow mr-4">
                <h3 className="text-lg font-medium mb-1">{item.name}</h3>
                <div className="text-gray-700">{item.price.toFixed(2)} лв</div>
              </div>
              
              <div className="flex items-center space-x-2 mr-6">
                <button 
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100"
                  onClick={() => handleUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                  aria-label="Намаляване на количеството"
                >
                  -
                </button>
                <span className="w-8 text-center">{item.quantity}</span>
                <button 
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100"
                  onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                  aria-label="Увеличаване на количеството"
                >
                  +
                </button>
              </div>
              
              <div className="w-24 text-right font-medium">
                {(item.price * item.quantity).toFixed(2)} лв
              </div>
              
              <button 
                className="absolute top-5 right-0 text-gray-400 hover:text-red-500 text-xl font-bold w-8 h-8 flex items-center justify-center"
                onClick={() => handleRemoveItem(item.id)}
                aria-label="Премахване на продукт"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        
        <div className="bg-gray-50 rounded-lg p-6 h-fit">
          <h3 className="text-xl font-bold mb-6">Обобщение на поръчката</h3>
          
          <div className="flex justify-between py-3">
            <span>Междинна сума</span>
            <span>{cart.subtotal.toFixed(2)} лв</span>
          </div>
          
          {cart.coupon && (
            <div className="flex justify-between py-3 text-green-600">
              <span className="flex items-center">
                Отстъпка ({cart.coupon.code})
                <button 
                  className="ml-2 text-xs text-gray-500 underline hover:text-gray-700"
                  onClick={handleRemoveCoupon}
                >
                  Премахни
                </button>
              </span>
              <span>-{cart.discountAmount.toFixed(2)} лв</span>
            </div>
          )}
          
          <div className="flex justify-between py-3">
            <span>Доставка</span>
            <span className="text-sm text-gray-500">Изчислява се при плащане</span>
          </div>
          
          <div className="flex justify-between py-3 border-t border-gray-300 font-bold text-lg mt-2">
            <span>Приблизителна обща сума</span>
            <span>{(cart.subtotal - (cart.discountAmount || 0)).toFixed(2)} лв</span>
          </div>
          
          <div className="mt-6 mb-6">
            <div className="flex">
              <input
                type="text"
                placeholder="Код за купон"
                value={couponCode}
                onChange={handleCouponChange}
                disabled={isApplyingCoupon || !!cart.coupon}
                className="flex-grow px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
              />
              <button
                className="bg-gray-800 text-white px-4 py-2 rounded-r-md disabled:bg-gray-400 hover:bg-gray-700 transition"
                onClick={handleApplyCoupon}
                disabled={isApplyingCoupon || !couponCode.trim() || !!cart.coupon}
              >
                {isApplyingCoupon ? 'Прилагане...' : 'Приложи'}
              </button>
            </div>
            {couponError && <div className="mt-2 text-sm text-red-600">{couponError}</div>}
          </div>
          
          <button 
            className="w-full bg-primary text-white py-3 rounded-md hover:bg-primary/80 transition mb-4"
            onClick={handleCheckout}
          >
            Продължи към плащане
          </button>
          
          <Link to="/" className="block text-center text-primary hover:underline">Продължи с пазаруването</Link>
        </div>
      </div>
    </div>
  );
};

export default Cart; 